/**
 * Type definitions and constants for LogViewer
 */

import type { LogEvent, LogLevel } from '@/types/dashboard';

// =============================================================================
// Constants
// =============================================================================

/** Identifier for logs that don't have a requestId */
export const UNGROUPED_REQUEST_ID = 'ungrouped';

/** Log group paths mapped to user-friendly display names */
export const LOG_GROUP_LABELS: Record<string, string> = {
  '/aws/lambda/nb-rag-arts-way-chat': 'Chat',
  '/aws/lambda/nb-rag-arts-way-classification': 'Classification',
  '/aws/lambda/nb-rag-arts-way-dashboard': 'Dashboard',
  '/aws/lambda/nb-rag-arts-way-ingest': 'Ingest',
  '/aws/lambda/nb-rag-arts-way-management': 'Management',
};

// =============================================================================
// Types
// =============================================================================

/** Lambda execution metrics extracted from REPORT logs */
export interface RequestMetrics {
  /** Actual execution duration in milliseconds */
  duration?: number;
  /** Billed duration in milliseconds (rounded up to nearest 1ms) */
  billedDuration?: number;
  /** Configured memory size in MB */
  memorySize?: number;
  /** Peak memory used during execution in MB */
  maxMemoryUsed?: number;
  /** Cold start initialization time in milliseconds */
  initDuration?: number;
}

/** A group of logs from a single Lambda invocation */
export interface RequestGroup {
  /** The Lambda request ID (UUID) or UNGROUPED_REQUEST_ID */
  requestId: string;
  /** User logs only (INFO, WARN, ERROR) - excludes system logs */
  logs: LogEvent[];
  /** ISO timestamp of the earliest log in the group */
  startTime: string;
  /** ISO timestamp of the latest log in the group */
  endTime: string;
  /** Execution metrics extracted from REPORT log */
  metrics: RequestMetrics;
  /** Whether any log in the group has ERROR level */
  hasError: boolean;
  /** Whether any log in the group has WARN level */
  hasWarning: boolean;
  /** Whether this invocation was a cold start */
  isColdStart: boolean;
}

/** View mode for log display */
export type ViewMode = 'flat' | 'grouped';

/** Log level filter options */
export type LogLevelFilter = 'ALL' | LogLevel;
